import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert, Tabs, Tab, Card } from 'react-bootstrap';
import axios from 'axios';
import { PayPalButtons } from '@paypal/react-paypal-js';
import CryptoTab from '../CryptoTab'; // Import your CryptoTab component
import { SUBSCRIPTION_ENDPOINTS } from '../../utils/constants';

const PaymentVerificationModal = ({ 
  show, 
  onHide, 
  userDetails, 
  token,
  onSuccess, 
  onError 
}) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('paypal'); // Default to PayPal initially
  const [paystackButtonDisabled, setPaystackButtonDisabled] = useState(false);
  const [country, setCountry] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);

  // Hard-coded Rand prices (same as PaymentModal)
  const randPrices = {
    1: 100, // Basic plan price in Rand
    2: 370  // Plus plan price in Rand
  };

  // Detect user's location and set default payment method
  useEffect(() => {
    const detectLocation = async () => {
      try {
        setLocationLoading(true);
        
        // Using ipapi.co for free geolocation (you can also use other services)
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        setCountry(data.country_code);
        
        // Set default tab based on location
        if (data.country_code === 'ZA') { // South Africa
          setActiveTab('paystack');
        } else {
          setActiveTab('paypal');
        }
      } catch (error) {
        console.error('Error detecting location:', error);
        // Default to PayPal if location detection fails
        setActiveTab('paypal');
        setCountry('UNKNOWN');
      } finally {
        setLocationLoading(false);
      }
    };

    // Only detect location when modal is shown
    if (show) {
      detectLocation();
    }
  }, [show]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setLoading(false);
      setPaystackButtonDisabled(false);
      setLocationLoading(true);
      onError(null); // Clear any existing errors
    }
  }, [show, onError]);

  // Get the correct price based on location and payment method
  const getPriceDisplay = () => {
    if (!userDetails) return '';
    
    // For South Africa, default to Rand pricing
    if (country === 'ZA') {
      if (activeTab === 'paystack') {
        return `R${randPrices[userDetails.planId]?.toFixed(2) || '0.00'}`;
      } else {
        // Even for PayPal in SA, show both currencies
        return `$${parseFloat(userDetails.planPrice).toFixed(2)}`;
      }
    } else {
      // For other countries, default to USD pricing
      if (activeTab === 'paypal' || activeTab === 'crypto') {
        // return `$${parseFloat(userDetails.planPrice).toFixed(2)}`;
        return 1;
      } else {
        // For Paystack outside SA, show both currencies
        return `R${randPrices[userDetails.planId]?.toFixed(2) || '0.00'}`;
      }
    }
  };

  // Get the actual amount to charge
  const getChargeAmount = () => {
    if (!userDetails) return 0;
    
    if (activeTab === 'paystack') {
      // Use Rand amount for Paystack
      return randPrices[userDetails.planId] || 0;
    } else {
      // Use USD amount for PayPal and Crypto
      // return parseFloat(userDetails.planPrice);
      return 1;
    }
  };

  // Get location-based messaging
  const getLocationMessage = () => {
    if (locationLoading) return 'Detecting your location...';
    if (country === 'ZA') return 'Payment options for South Africa';
    if (country === 'UNKNOWN') return 'Payment options (location detection failed)';
    return 'International payment options';
  };

  // Handle crypto payment success
 const handleCryptoPaymentSuccess = async (paymentData) => {
  try {

    onSuccess({
      paymentMethod: 'crypto',
      paymentId: paymentData.paymentId || paymentData.payment_id,
      token: paymentData.token,
      amount: paymentData.amount,
      chainId: paymentData.chainId,
      planId: paymentData.planId,
      contractAddress: paymentData.contractAddress
    });
    
  } catch (error) {
    console.error('Error processing crypto payment success:', error);
    onError('Payment submitted successfully. You will receive an email confirmation once verified.');
  }
};

  // Handle crypto payment error
  const handleCryptoPaymentError = (error) => {
    onError(`Crypto payment failed: ${error}`);
  };

  // Handle Paystack payment
  const handlePaystackPayment = async () => {
    if (!userDetails) {
      onError('User details missing');
      return;
    }
    
    setLoading(true);
    setPaystackButtonDisabled(true);
    
    try {
      const chargeAmount = getChargeAmount(); // Get the correct Rand amount
      
      localStorage.setItem('userEmail', userDetails.email);
      
      // Store verification token for Paystack return
      sessionStorage.setItem('verificationToken', token);
      sessionStorage.setItem('planId', userDetails.planId);
      
      localStorage.setItem('userEmail', userDetails.email);
      
      // Initialize payment with Paystack - Use verification page as callback
      const initResponse = await axios.post(
        SUBSCRIPTION_ENDPOINTS.INIT_PAYMENT,
        {
          email: userDetails.email,
          amount: chargeAmount,
          planId: userDetails.planId,
          callback_url: `${window.location.origin}/#/payment-verification/${token}?status=paystack_return`,
          cancel_action: `${window.location.origin}/#/payment-verification/${token}?status=cancelled`
        }
      );
      
      if (initResponse.data.status && initResponse.data.data && initResponse.data.data.authorization_url) {
        // Store reference for verification on return
        sessionStorage.setItem('paymentRef', initResponse.data.data.reference);
        sessionStorage.setItem('planId', userDetails.planId);
                
        // Redirect to Paystack payment page
        window.location.href = initResponse.data.data.authorization_url;
      } else {
        onError('Failed to initialize payment. Please try again.');
        setPaystackButtonDisabled(false);
      }
    } catch (error) {
      console.error('Error initializing payment:', error);
      onError('Error initializing payment. Please try again.');
      setPaystackButtonDisabled(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle PayPal payment
  const createPayPalOrder = async () => {
    if (!userDetails) {
      onError('User details missing');
      return;
    }
    
    try {
      const chargeAmount = getChargeAmount(); // Get the correct USD amount
            
      // Create PayPal order
      const response = await axios.post(
        SUBSCRIPTION_ENDPOINTS.CREATE_PAYPAL_ORDER,
        {
          amount: chargeAmount,
          name: userDetails.planName,
          email: userDetails.email
        }
      );
      
      // Return order ID to PayPal SDK for processing
      if (response.data && response.data.id) {
        return response.data.id;
      } else {
        throw new Error('Failed to create PayPal order');
      }
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      onError('Error creating PayPal order. Please try again.');
      return null;
    }
  };

  // Handle PayPal order approval
  const onPayPalApprove = async (data) => {
    setLoading(true);
    
    try {
      // Capture the approved PayPal order
      const captureResponse = await axios.post(
        SUBSCRIPTION_ENDPOINTS.CAPTURE_PAYPAL_ORDER,
        {
          orderID: data.orderID,
          payerID: data.payerID,
          email: userDetails.email,
          planId: userDetails.planId
        }
      );
      
      if (captureResponse.data.status === 'COMPLETED') {
        // Call the success handler with payment data
        onSuccess({
          orderID: data.orderID,
          payerID: data.payerID,
          planId: userDetails.planId
        });
      } else {
        onError('Payment was not completed. Please try again.');
      }
    } catch (error) {
      console.error('Error processing PayPal payment:', error);
      onError('Error processing payment. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Handle PayPal errors
  const onPayPalError = (err) => {
    console.error('PayPal error:', err);
    onError('An error occurred with PayPal. Please try again.');
  };

  // Handle PayPal cancellation
  const onPayPalCancel = () => {
    // No need to show error, user just cancelled
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Complete Payment & Verification</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {userDetails ? (
          <>
            <div className="plan-summary mb-4">
              <Card className="border-success">
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">Complete Your Registration</h5>
                  <small className="text-light">{getLocationMessage()}</small>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Plan:</span>
                    <strong>{userDetails.planName}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Price:</span>
                    <strong>{getPriceDisplay()}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Period:</span>
                    <strong>Annual</strong>
                  </div>
                  <Alert variant="info" className="mt-3 mb-0">
                    <i className="fas fa-info-circle mr-2"></i>
                    Complete this payment to verify your email and activate your account.
                  </Alert>
                </Card.Body>
              </Card>
            </div>
            
            {locationLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Setting up payment options...</p>
              </div>
            ) : (
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4"
                fill
              >
                <Tab 
                  eventKey="paystack" 
                  title={
                    <>
                      <i className="fas fa-credit-card mr-2"></i>
                      Paystack {country === 'ZA' && <span className="badge badge-success ml-1">Recommended</span>}
                    </>
                  }
                >
                  <div className="paystack-tab p-3">
                    <p className="mb-4 text-white">
                      Pay securely using Paystack in South African Rand. 
                      {country === 'ZA' 
                        ? ' This is the recommended option for South African customers.' 
                        : ' Note: You will be charged in South African Rand.'
                      }
                    </p>
                    
                    <Button
                      variant="success"
                      size="lg"
                      block
                      onClick={handlePaystackPayment}
                      disabled={paystackButtonDisabled || loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="mr-2"
                          />
                          Processing...
                        </>
                      ) : (
                        <>Pay with Paystack - R{randPrices[userDetails.planId]?.toFixed(2) || '0.00'}</>
                      )}
                    </Button>
                  </div>
                </Tab>
                
                <Tab 
                  eventKey="paypal" 
                  title={
                    <>
                      <i className="fab fa-paypal mr-2"></i>
                      PayPal {country !== 'ZA' && country !== 'UNKNOWN' && <span className="badge badge-primary ml-1">Recommended</span>}
                    </>
                  }
                >
                  <div className="paypal-tab p-3">
                    <p className="mb-4 text-white">
                      Pay securely using PayPal in US Dollars. You can use your PayPal account or a credit card.
                      {country !== 'ZA' && country !== 'UNKNOWN' && ' This is the recommended option for international customers.'}
                    </p>
                    
                    {loading ? (
                      <div className="text-center py-3">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-white">Processing payment...</p>
                      </div>
                    ) : (
                      <PayPalButtons
                        style={{
                          color: 'blue',
                          shape: 'rect',
                          label: 'pay',
                          height: 50,
                        }}
                        createOrder={createPayPalOrder}
                        onApprove={onPayPalApprove}
                        onError={onPayPalError}
                        onCancel={onPayPalCancel}
                      />
                    )}
                  </div>
                </Tab>

                {/* <Tab 
                  eventKey="crypto" 
                  title={
                    <>
                      <i className="fab fa-bitcoin mr-2"></i>
                      Cryptocurrency
                    </>
                  }
                >
                  <CryptoTab
                    userDetails={userDetails}
                    loading={loading}
                    onPaymentSuccess={handleCryptoPaymentSuccess}
                    onPaymentError={handleCryptoPaymentError}
                  />
                </Tab> */}
              </Tabs>
            )}

            <div className="text-center mt-3">
              <small className="text-muted">
                After successful payment, your account will be automatically verified and activated.
                {country && country !== 'UNKNOWN' && (
                  <div className="mt-1">
                    Detected location: {country === 'ZA' ? '🇿🇦 South Africa' : `🌍 ${country}`}
                  </div>
                )}
              </small>
            </div>
          </>
        ) : (
          <Alert variant="info">
            Loading payment details...
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PaymentVerificationModal;